import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { type ContentBlock, parseContentBlocks } from '@/utils/contentBlocks';

import { styles } from './MessageContent.constants';
import type { MessageContentProps, ToolUseBlockViewProps } from './MessageContent.types';

const blockKey = (block: ContentBlock, index: number): string =>
	block.type === 'tool_use' && block.id ? block.id : `${block.type}-${index}`;

const ToolUseBlockView = ({ name, input }: ToolUseBlockViewProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });

	return (
		<details className={styles.toolBlock} open>
			<summary className={styles.toolSummary}>
				<ChevronRight className="h-sm w-sm text-subtle transition-transform group-open:rotate-90" />
				<code className={styles.toolName}>{name}</code>
			</summary>
			<div className={styles.toolBody}>
				<p className={styles.toolLabel}>{t('TOOL_INPUT')}</p>
				<pre className={styles.toolPre}>{JSON.stringify(input, null, 2)}</pre>
			</div>
		</details>
	);
};

const BlockItem = ({ block }: { block: ContentBlock }) => {
	if (block.type === 'text') return <p className={styles.text}>{block.text}</p>;
	if (block.type === 'tool_use') return <ToolUseBlockView name={block.name} input={block.input} />;
	return <pre className={styles.rawPre}>{JSON.stringify(block.raw, null, 2)}</pre>;
};

/**
 * Renders a transcript message body. When the content is the JSON dump of
 * Anthropic content blocks (bot notes), it renders text blocks as paragraphs
 * and tool_use blocks as collapsible cards; any other content falls back to
 * the plain string, preserving the previous behaviour.
 */
export const MessageContent = ({ content }: MessageContentProps) => {
	const blocks = parseContentBlocks(content);

	if (!blocks) return <span>{content}</span>;
	return (
		<div className={styles.container}>
			{blocks.map((block, index) => (
				<BlockItem key={blockKey(block, index)} block={block} />
			))}
		</div>
	);
};
